/**
 * The sidebar inside a programme: its five surfaces instead of the tenant's
 * destinations.
 *
 * Swapped rather than nested, because inside a programme the tenant's
 * destinations are all "leave what you are doing", and the five surfaces are
 * the whole of what there is to do.
 *
 * @module components/shell/ProgrammeSidebar
 */

import React from "react";

import { BrandWordmark } from "@/components/shared/BrandWordmark";
import { TONE_DOT } from "@/components/shared/tone-presentation";
import { WorkflowStateBadge } from "@/components/shared/WorkflowStateBadge";
import { Heading, Panel, Sidebar, SidebarNavItem, SidebarSection, Text } from "@/components/ui";
import { useProgramme } from "@/hooks/useStore";
import { usePublishedReadiness } from "@/platform/editor-readiness-context";
import { submissionPackStatus, SURFACES, surfaceStatus } from "@/router/programme-editor/surfaces";
import type { ReadinessResult } from "@/server/types/readiness";
import type { WorkflowState } from "@/server/types/workflow";
import type { Programme } from "@/types";
import { validateProgramme, type ValidationFlag } from "@/utils/validation";

import { SidebarFooter } from "./SidebarFooter";

export interface ProgrammeSidebarProps {
  /** `/programmes/<id>`, or the change case's own base. */
  base: string;
  title: string;
  workflowState: WorkflowState;
  /** The open programme, for the per-surface status dots. */
  programme: Programme;
  version: string;
  /** The surface keys this reader reaches; every surface when absent. */
  surfaces?: readonly string[];
  /** The server's readiness, which the submission pack's dot answers to. */
  readiness?: ReadinessResult | null;
}

export function ProgrammeSidebar({
  base,
  title,
  workflowState,
  programme,
  version,
  surfaces: reachable = undefined,
  readiness = null,
}: ProgrammeSidebarProps): React.ReactElement {
  // Once a render: every surface's dot reads every one of its steps.
  const flags = validateProgramme(programme) as ValidationFlag[];
  const groups: readonly ["primary" | "oversight", string | undefined][] = [
    ["primary", undefined],
    ["oversight", "Oversight"],
  ];

  return (
    <Sidebar
      data-testid="programme-sidebar"
      header={<BrandWordmark size="lg" className="leading-none" />}
      context={
        <div className="flex flex-col gap-2" data-testid="programme-context">
          <SidebarNavItem
            to="/programmes"
            end
            label="Back to programmes"
            icon="arrow-left"
            data-testid="programme-sidebar-back"
          />
          <Panel tone="inset" flush className="p-3">
            <Heading level={3} data-testid="programme-context-title">
              {title}
            </Heading>
            {/* Named, because the editor bar shows readiness in the same chip style. */}
            <div className="mt-2 flex flex-col items-start gap-1">
              <Text as="span" size="sm" tone="muted">
                Workflow
              </Text>
              <WorkflowStateBadge
                state={workflowState}
                data-testid="programme-context-workflow-state"
              />
            </div>
          </Panel>
        </div>
      }
      footer={<SidebarFooter version={version} />}
    >
      {groups.map(([group, label]) => {
        const surfaces = SURFACES.filter(
          (surface) => surface.group === group && (!reachable || reachable.includes(surface.key)),
        );
        const items = surfaces.map((surface) => {
          const own = surfaceStatus(surface, programme, flags);
          const status = surface.key === "submission" ? submissionPackStatus(own, readiness) : own;
          return (
            <SidebarNavItem
              key={surface.key}
              // The surface's own address, which forwards to its first section, so
              // the item stays current on every section rather than the first.
              to={`${base}/${surface.segment}`}
              label={surface.title}
              icon={surface.icon}
              notUnder={[]}
              status={TONE_DOT[status.tone]}
              statusLabel={status.label}
              data-testid={`surface-tab-${surface.segment}`}
            />
          );
        });
        return label ? (
          <SidebarSection key={group} label={label}>
            {items}
          </SidebarSection>
        ) : (
          <SidebarSection key={group}>{items}</SidebarSection>
        );
      })}
    </Sidebar>
  );
}

/**
 * The sidebar over the programme open in the editor, following its edits: the
 * shell that renders it does not re-render on a store change.
 */
export function OpenProgrammeSidebar(
  props: Omit<ProgrammeSidebarProps, "title" | "programme">,
): React.ReactElement {
  const { programme } = useProgramme();
  const readiness = usePublishedReadiness();
  return (
    <ProgrammeSidebar
      {...props}
      readiness={readiness}
      title={programme.title || "Untitled programme"}
      programme={programme}
    />
  );
}
